import type { VolumeRecord } from 'types/api-responses';
import type { ForecastData } from '../types';

/**
 * Amount to divide monthly production / forecast volumes by to get their average daily rates.
 */
export const DAILY_DIVISOR = 30.4;

/**
 * A monthly volume as an average daily rate.
 *
 * @param {number} monthlyVolume - The month's volume.
 * @return {number} The average daily rate.
 */
export function monthlyVolumeToDaily(monthlyVolume: number): number {
  return monthlyVolume / DAILY_DIVISOR;
}

/**
 * An average daily rate as a monthly volume.
 *
 * @param {number} dailyVolume - The average daily rate.
 * @return {number} The month's volume.
 */
export function dailyVolumeToMonthly(dailyVolume: number): number {
  return dailyVolume * DAILY_DIVISOR;
}

/** How a monthly point's date reads, e.g. "March 2026". */
const MONTH_DATE_FORMAT = 'MMMM YYYY';
/** How a daily point's date reads, e.g. "March 4, 2026". */
const DAY_DATE_FORMAT = 'MMMM D, YYYY';

/**
 * The moment format for a point's date: with the day for daily rows, month
 * and year otherwise.
 *
 * @param {boolean} isDailyRate - Whether the points are daily rows.
 * @return {string} The moment format.
 */
export function pointDateFormat(isDailyRate: boolean): string {
  return isDailyRate ? DAY_DATE_FORMAT : MONTH_DATE_FORMAT;
}

/**
 * Converts one monthly volume record's streams to average daily rates,
 * leaving its date and any other keys alone.
 *
 * @param {T} record - A monthly volume record, such as production or a type curve month.
 * @return {T} The same record with oil/gas/water as daily rates.
 */
export function toDailyRate<
  T extends {
    oil: number | null | undefined;
    gas: number | null | undefined;
    water: number | null | undefined;
  },
>(record: T): T {
  /**
   * One stream's daily rate, leaving a missing value missing.
   *
   * @param {number | null | undefined} value - The stream's monthly volume.
   * @return {number | null | undefined} The daily rate.
   */
  function scale(value: number | null | undefined) {
    if (value === null || value === undefined) return value;
    return monthlyVolumeToDaily(value);
  }
  return {
    ...record,
    oil: scale(record.oil),
    gas: scale(record.gas),
    water: scale(record.water),
  };
}

/**
 * Production as the chart shows it. Monthly volumes become daily rates when
 * the chart shows daily rates. Daily production is already daily, so it is
 * left as it is.
 *
 * @param {T} production - The production, with its volumes.
 * @param {boolean} dailyScale - Whether the chart shows daily rates.
 * @param {boolean} isDailyRate - Whether the volumes are already daily rows.
 * @return {T} The production with the volumes to draw.
 */
export function displayedProduction<T extends { volume: VolumeRecord[] }>(
  production: T,
  dailyScale: boolean,
  isDailyRate: boolean
): T {
  if (!dailyScale || isDailyRate) return production;
  return { ...production, volume: production.volume.map(toDailyRate) };
}

/**
 * Forecasts and extra production lines as the chart shows them, scaled the
 * same way as the production.
 *
 * @param {ForecastData[]} forecasts - The lines to draw.
 * @param {boolean} dailyScale - Whether the chart shows daily rates.
 * @return {ForecastData[]} The lines with the volumes to draw.
 */
export function displayedForecasts(
  forecasts: ForecastData[],
  dailyScale: boolean
): ForecastData[] {
  if (!dailyScale) return forecasts;
  return forecasts.map((forecast) =>
    forecast.volumeIsDailyRate
      ? forecast
      : { ...forecast, volume: forecast.volume.map(toDailyRate) }
  );
}
